import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../constants";

const STATUS = {
  ok: { color: "#A3E635", label: "OK", icon: "✓" },
  warn: { color: "#FBBF24", label: "WARN", icon: "!" },
  crit: { color: "#F87171", label: "CRIT", icon: "✕" },
};

const CATEGORY_LABEL = {
  internal: "Internal",
  security: "Security",
  features: "Features",
  external: "External",
  client: "Client / Network",
};

const Pill = ({ status }) => {
  const s = STATUS[status] || STATUS.warn;
  return (
    <span
      className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold"
      style={{ background: s.color, color: "#000", boxShadow: `0 0 8px ${s.color}80` }}
      title={s.label}
    >
      {s.icon}
    </span>
  );
};

/**
 * Collects client-side diagnostic data: network signal, online status, browser, latency.
 * Runs entirely in-browser (the server can't observe the user's WiFi/LTE).
 */
function collectClientChecks(latencyMs) {
  const results = [];
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

  // Online status
  results.push({
    category: "client",
    name: "Network",
    status: navigator.onLine ? "ok" : "crit",
    message: navigator.onLine ? "Online" : "Offline — no internet",
  });

  // Signal strength via Network Information API
  if (conn) {
    const t = conn.effectiveType || "?";
    const downlink = conn.downlink ?? null;
    const rtt = conn.rtt ?? null;

    // Effective type: 4g/3g/2g/slow-2g
    const typeStatus = t === "4g" ? "ok" : t === "3g" ? "warn" : "crit";
    results.push({
      category: "client",
      name: "Connection type",
      status: typeStatus,
      message: `${t.toUpperCase()}${downlink ? ` · ${downlink} Mbps` : ""}${rtt ? ` · ${rtt}ms RTT` : ""}`,
      value: t,
    });

    if (downlink !== null) {
      const dlStatus = downlink >= 5 ? "ok" : downlink >= 1.5 ? "warn" : "crit";
      results.push({
        category: "client",
        name: "Bandwidth",
        status: dlStatus,
        message: `${downlink} Mbps downlink`,
        value: downlink,
      });
    }
    if (conn.saveData) {
      results.push({
        category: "client",
        name: "Data Saver",
        status: "warn",
        message: "Data saver enabled — bandwidth-heavy features may be limited",
      });
    }
  } else {
    results.push({
      category: "client",
      name: "Connection info",
      status: "warn",
      message: "Network Information API not available in this browser",
    });
  }

  // Backend latency
  if (latencyMs != null) {
    const latStatus = latencyMs < 500 ? "ok" : latencyMs < 1500 ? "warn" : "crit";
    results.push({
      category: "client",
      name: "Backend latency",
      status: latStatus,
      message: `${latencyMs}ms round-trip to backend`,
      value: latencyMs,
    });
  }

  // Browser
  const ua = navigator.userAgent;
  results.push({
    category: "client",
    name: "Browser",
    status: "ok",
    message: ua.length > 70 ? ua.slice(0, 67) + "…" : ua,
  });

  // Service worker / installable PWA hint
  if ("serviceWorker" in navigator) {
    results.push({ category: "client", name: "Service Worker support", status: "ok", message: "Available" });
  } else {
    results.push({ category: "client", name: "Service Worker support", status: "warn", message: "Not available — limited offline behavior" });
  }

  return results;
}

export default function SiteDiagnosticsCard({ adminPassword }) {
  const [enabled, setEnabled] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const timer = useRef(null);

  const run = useCallback(async () => {
    if (!adminPassword) return;
    setRunning(true);
    const t0 = performance.now();
    try {
      const res = await axios.post(`${API}/admin/diagnostics/run`, { password: adminPassword });
      const latency = Math.round(performance.now() - t0);
      const server = res.data;
      const clientResults = collectClientChecks(latency);
      const allResults = [...server.results, ...clientResults];
      // Recompute tally + overall
      const tally = { ok: 0, warn: 0, crit: 0, total: allResults.length };
      allResults.forEach((r) => { tally[r.status] = (tally[r.status] || 0) + 1; });
      const overall = tally.crit ? "crit" : tally.warn ? "warn" : "ok";
      setData({ ...server, results: allResults, tally, overall });
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Diagnostic run failed");
    } finally {
      setRunning(false);
    }
  }, [adminPassword]);

  // When toggled on: run once, then every 30s
  useEffect(() => {
    if (!enabled) {
      if (timer.current) clearInterval(timer.current);
      timer.current = null;
      return;
    }
    run();
    timer.current = setInterval(run, 30000);
    return () => {
      if (timer.current) clearInterval(timer.current);
      timer.current = null;
    };
  }, [enabled, run]);

  const grouped = (data?.results || []).reduce((acc, r) => {
    (acc[r.category] = acc[r.category] || []).push(r);
    return acc;
  }, {});

  const toggleSection = (cat) => setCollapsed((c) => ({ ...c, [cat]: !c[cat] }));

  return (
    <div className="card-glass p-6 mb-8" data-testid="site-diagnostics-card">
      {/* Header with toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          {data ? <Pill status={data.overall} /> : <Pill status="warn" />}
          <div>
            <h2 className="font-heading text-xl font-semibold text-white">Site Diagnostics</h2>
            <p className="text-[#E8E4DC]/60 text-xs">
              {enabled
                ? `Auto-running every 30s · ${data?.tally?.total ?? "—"} checks`
                : "Toggle on to run comprehensive internal + external diagnostics"}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {data && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-[#A3E635]" title="OK"><Pill status="ok"/> {data.tally.ok}</span>
              <span className="text-[#FBBF24]" title="Warnings"><Pill status="warn"/> {data.tally.warn}</span>
              <span className="text-[#F87171]" title="Critical"><Pill status="crit"/> {data.tally.crit}</span>
            </div>
          )}
          <button
            onClick={() => run()}
            disabled={running || !enabled}
            className="text-xs px-3 py-1 rounded-full border border-white/15 text-[#A3E635] hover:bg-[#A3E635]/10 disabled:opacity-40 transition-colors"
            data-testid="diagnostics-run-now"
          >
            {running ? "Running…" : "Run Now"}
          </button>
          {/* Toggle switch */}
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            onClick={() => {
              setEnabled((v) => {
                const next = !v;
                toast.success(next ? "Diagnostics enabled" : "Diagnostics paused");
                return next;
              });
            }}
            className={`relative inline-flex h-7 w-14 rounded-full transition-colors ${enabled ? "bg-[#A3E635]" : "bg-white/15"}`}
            data-testid="diagnostics-toggle"
          >
            <span
              className={`inline-block h-5 w-5 rounded-full bg-white shadow-md transition-transform ${enabled ? "translate-x-8" : "translate-x-1"}`}
              style={{ marginTop: "4px" }}
            />
          </button>
        </div>
      </div>

      {/* Body */}
      {!enabled && !data && (
        <div className="text-[#E8E4DC]/60 text-sm py-6 text-center border border-dashed border-white/10 rounded-lg" data-testid="diagnostics-idle">
          Flip the switch above to start diagnostics. Tests include database, disk, memory, security, SSL cert, integrations, ZIP artifacts, and client-side network signal.
        </div>
      )}

      {error && (
        <div className="text-red-400 text-sm mb-3 p-3 rounded bg-red-500/10 border border-red-500/30" data-testid="diagnostics-error">
          {error}
        </div>
      )}

      {data && (
        <>
          <div className="text-[#E8E4DC]/50 text-xs mb-4">
            Last run {new Date(data.checked_at).toLocaleString()}
          </div>

          {Object.entries(grouped).map(([cat, items]) => {
            const critCount = items.filter((i) => i.status === "crit").length;
            const warnCount = items.filter((i) => i.status === "warn").length;
            const sectionStatus = critCount ? "crit" : warnCount ? "warn" : "ok";
            const isCollapsed = collapsed[cat];
            return (
              <div key={cat} className="mb-3" data-testid={`diagnostics-section-${cat}`}>
                <button
                  onClick={() => toggleSection(cat)}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <Pill status={sectionStatus} />
                    <span className="text-white font-semibold uppercase text-xs tracking-wider">{CATEGORY_LABEL[cat] || cat}</span>
                    <span className="text-[#E8E4DC]/40 text-xs">{items.length}</span>
                  </span>
                  <svg className={`w-4 h-4 text-[#E8E4DC]/60 transition-transform ${isCollapsed ? "" : "rotate-180"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {!isCollapsed && (
                  <ul className="mt-1.5 space-y-0.5">
                    {items.map((r, i) => (
                      <li
                        key={`${cat}-${r.name}-${i}`}
                        className="flex items-start gap-3 px-3 py-2 rounded hover:bg-white/3 text-sm"
                        data-testid={`diagnostics-row-${r.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      >
                        <Pill status={r.status} />
                        <div className="flex-1 min-w-0">
                          <div className="text-white">{r.name}</div>
                          <div className="text-[#E8E4DC]/60 text-xs truncate">{r.message}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
